import CapabilityPage from './CapabilityPage'
import {
  AI_HUB_MODEL_PRICING,
  type AIHubModelPrice,
} from '../lib/aiHubModelPricing'

const LLM_TITLE = 'Token Factory for Commercial AI Inference | ShimmerTech'
const LLM_DESCRIPTION =
  'ShimmerTech hosts and operates open-source large-language models for commercial AI inference through managed model APIs and routing. Inference only; no training or fine-tuning.'

const LLM_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': 'https://shimmertech.net/#llm-service',
  name: 'ShimmerTech Token Factory',
  serviceType: 'Managed open-source LLM inference APIs and model routing',
  provider: { '@id': 'https://shimmertech.net/#organization' },
  url: 'https://shimmertech.net/',
  description: LLM_DESCRIPTION,
}

const MODEL_PRICE_GROUPS = AI_HUB_MODEL_PRICING.reduce<Record<string, AIHubModelPrice[]>>((groups, item) => {
  if (!groups[item.provider]) groups[item.provider] = []
  groups[item.provider].push(item)
  return groups
}, {})

const MODEL_PRICE_GROUP_ENTRIES = Object.entries(MODEL_PRICE_GROUPS)

function formatModelPrice(value: number) {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: value < 1 ? 2 : 2,
    maximumFractionDigits: value < 1 ? 3 : 3,
  })}`
}

function LlmPricingTable() {
  return (
    <section id="llm-model-pricing" className="od-cap-model-pricing" aria-labelledby="llm-model-pricing-title">
      <div className="od-cap-model-pricing-head">
        <div>
          <p className="od-cap-eyebrow">OpenRouter models</p>
          <h2 id="llm-model-pricing-title">LLM model pricing</h2>
        </div>
        <p>
          Reference OpenRouter rates for planning AI products. Prices are shown
          in USD per 1M input and output tokens.
        </p>
      </div>

      <div className="od-cap-model-provider-list">
        {MODEL_PRICE_GROUP_ENTRIES.map(([provider, models]) => (
          <article className="od-cap-model-provider" key={provider}>
            <header>
              <h3>{provider}</h3>
              <span>{models.length} models</span>
            </header>
            <div className="od-cap-model-table-wrap">
              <table className="od-cap-model-table">
                <thead>
                  <tr>
                    <th scope="col">Model ID</th>
                    <th scope="col">Input / 1M</th>
                    <th scope="col">Output / 1M</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr key={`${provider}-${model.model}`}>
                      <td data-label="Model ID">
                        <code>{model.model}</code>
                      </td>
                      <td data-label="Input / 1M">{formatModelPrice(model.input)}</td>
                      <td data-label="Output / 1M">{formatModelPrice(model.output)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

export default function Llm() {
  return (
    <CapabilityPage
      path="/"
      title={LLM_TITLE}
      description={LLM_DESCRIPTION}
      eyebrow="Token factory"
      heroTitle="The token factory for commercial AI inference"
      heroBody="ShimmerTech hosts and operates open-source large-language models for business customers through managed model APIs and routing. Inference only: no training or fine-tuning, and customers receive API outputs without controlling the underlying GPUs."
      primaryCta="Talk to us"
      primaryHref="mailto:hi@shimmertech.net"
      secondaryCta="See pricing"
      secondaryTo="/#llm-model-pricing"
      visual="llm"
      heroImage="/capabilities/llm-hero.png"
      heroImageAlt="ShimmerTech LLM deployment visual with JSON contracts, model routing, and evaluation checkpoints"
      metrics={[
        { value: 'API', label: 'managed access to production model inference' },
        { value: 'routes', label: 'model selection and routing behind one endpoint' },
        { value: 'GPU', label: 'infrastructure operated entirely by ShimmerTech' },
      ]}
      featuresTitle="Managed inference, end to end"
      featuresBody="A production token layer for businesses that need reliable model outputs without owning or operating the underlying GPU fleet."
      features={[
        {
          title: 'Managed model APIs',
          body: 'Access open-source large-language models through stable APIs built for commercial inference workloads.',
          image: '/capabilities/llm-structured-outputs.webp',
          imageAlt: 'Engineer operating a managed model API beside production inference hardware',
        },
        {
          title: 'Production token serving',
          body: 'ShimmerTech runs the serving layer, capacity, observability, and reliability needed to produce tokens in production.',
          image: '/capabilities/llm-prompt-storage.webp',
          imageAlt: 'Production token-serving workflow open on an AI engineering workstation',
        },
        {
          title: 'Model routing',
          body: 'Route inference requests across supported models behind one managed endpoint and operating contract.',
          image: '/capabilities/llm-provider-flexibility.webp',
          imageAlt: 'Engineer routing model inference across modular GPU infrastructure',
        },
        {
          title: 'Infrastructure separation',
          body: 'Customers receive API outputs only. ShimmerTech retains control of the underlying GPUs, serving stack, and operations.',
          image: '/capabilities/llm-failure-envelopes.webp',
          imageAlt: 'Reliability engineer operating isolated production AI infrastructure',
        },
      ]}
      stepsTitle="From workload to tokens"
      steps={[
        {
          title: 'Scope',
          body: 'We map the inference workload, target models, latency needs, throughput, and API contract.',
        },
        {
          title: 'Provision',
          body: 'ShimmerTech selects and operates the serving infrastructure required for the agreed workload.',
        },
        {
          title: 'Serve',
          body: 'Customers integrate one managed API and receive model outputs without direct access to the GPU layer.',
        },
        {
          title: 'Operate',
          body: 'We monitor routing, capacity, reliability, and token delivery as production demand changes.',
        },
      ]}
      useCasesTitle="Built for commercial inference"
      useCases={[
        {
          title: 'AI product inference',
          body: 'Power customer-facing assistants, extraction systems, copilots, and automation through managed model APIs.',
        },
        {
          title: 'Enterprise model access',
          body: 'Give internal products a governed inference endpoint without building or operating a GPU platform.',
        },
        {
          title: 'Multi-model routing',
          body: 'Consolidate supported open-source models, routing policy, usage visibility, and operations behind one service.',
        },
      ]}
      jsonLd={LLM_JSONLD}
    >
      <LlmPricingTable />
    </CapabilityPage>
  )
}
